import type { PortalClientDto } from '@supply-mover-2-0/shared';
import { Select, useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useId } from 'react';
import {
	ACCOUNT_LOCATION_SEARCH_THRESHOLD,
	accountLocationOptions,
} from '../lib/accountLocationOptions';

/**
 * PD-14189 — single-select account/location picker for the storefront list
 * pages (Invoices, Orders, Transactions, Statements). The selection is still
 * single-value (the `clientId` query param is unchanged); the only change is
 * presentational: options are stacked under three alphabetized headings —
 * Direct (your accounts) / Children / Ownership — driven by the
 * `relationshipGroup` the backend assigns to each accessible account.
 *
 * PD-15027 — the shared Select rather than a native select, so the menu is
 * styled like the rest of the storefront and each account shows its customer
 * number on a second line. A customer with more than a handful of locations
 * also gets a search field at the top of the menu.
 *
 * Dumb/presentational by design: it does not read any store — the parent page
 * passes `clients` (already brand-scoped) and owns the change handler.
 */
interface AccountLocationPickerProps {
	value: string;
	onChange: (value: string) => void;
	clients: Array<PortalClientDto>;
	className?: string;
	label?: string;
}

export function AccountLocationPicker({
	value,
	onChange,
	clients,
	className,
	label,
}: AccountLocationPickerProps): JSX.Element {
	const { t } = useTranslation();
	const labelId = useId();

	return (
		<div className={className}>
			<label
				id={labelId}
				className="block text-xs font-medium text-brand-muted mb-1"
			>
				{t(label ?? 'Location')}
			</label>
			<Select
				aria-labelledby={labelId}
				value={value}
				onChange={onChange}
				options={accountLocationOptions(t, clients)}
				searchable={clients.length > ACCOUNT_LOCATION_SEARCH_THRESHOLD}
				searchPlaceholder={t('Search locations')}
			/>
		</div>
	);
}
